import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
/* COMPONENTS */
import ChevronLeft from '@mdi/svg/svg/chevron-left.svg?react';
import ChevronRight from '@mdi/svg/svg/chevron-right.svg?react';
import PageFirst from '@mdi/svg/svg/page-first.svg?react';
import PageLast from '@mdi/svg/svg/page-last.svg?react';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { KeyActions } from '../KeyActions';
import { MDIIcon } from '../MDIIcon';
import { type PaginationProps } from './DataTable.types';

// eslint-disable-next-line
// @ts-ignore
function _calculatePageCount({ length, rowsShown }) {
  let pageCount = 1;
  pageCount = Math.ceil(length / rowsShown);

  if (pageCount === 0) {
    return 1;
  }

  return pageCount;
}

const Pagination: React.FC<PaginationProps> = ({
  firstPageTitle = 'First Page',
  firstRow,
  lastPageTitle = 'Last Page',
  length,
  nextPageTitle = 'Next Page',
  ofPageText = 'of',
  onPageChange,
  pageNumber: sourcePageNumber,
  paginatedLength,
  prevPageTitle = 'Previous Page',
  rowsShown,
  sectionTitle = 'Table',
  hideLastPageOption,
  hideFirstPageOption,
}) => {
  const [pageCount, setPageCount] = useState(_calculatePageCount({ length, rowsShown }));
  // eslint-disable-next-line
  // @ts-ignore
  const canRunPaginationShortcuts = () => isEmpty(window.getSelection().toString());

  useEffect(() => {
    const newPageCount = _calculatePageCount({ length, rowsShown });
    setPageCount(newPageCount);
  }, [length, rowsShown]);

  const handleNextPage = () => {
    let pageNumber = sourcePageNumber;
    if (pageNumber < pageCount) {
      pageNumber += 1;
      // eslint-disable-next-line
      // @ts-ignore
      onPageChange?.(pageNumber);
    }
  };

  const handlePrevPage = () => {
    let pageNumber = sourcePageNumber;
    if (pageNumber > 1) {
      pageNumber -= 1;
      // eslint-disable-next-line
      // @ts-ignore
      onPageChange?.(pageNumber);
    }
  };

  // eslint-disable-next-line
  // @ts-ignore
  const handleFirstPage = () => onPageChange?.(1);

  const handleLastPage = () => {
    const lastPage = Math.ceil(length / paginatedLength);
    // eslint-disable-next-line
    // @ts-ignore
    onPageChange?.(lastPage);
  };

  const shortcuts = [
    {
      shortcut: 'shift+left',
      action: handleFirstPage,
      shouldActionRun: () => canRunPaginationShortcuts() && !hideFirstPageOption,
      info: {
        title: firstPageTitle,
        group: sectionTitle,
      },
    },
    {
      shortcut: 'left',
      action: handlePrevPage,
      shouldActionRun: canRunPaginationShortcuts,
      info: {
        title: prevPageTitle,
        group: sectionTitle,
      },
    },
    {
      shortcut: 'right',
      action: handleNextPage,
      shouldActionRun: canRunPaginationShortcuts,
      info: {
        title: nextPageTitle,
        group: sectionTitle,
      },
    },
    {
      shortcut: 'shift+right',
      action: handleLastPage,
      shouldActionRun: () => canRunPaginationShortcuts() && !hideLastPageOption,
      info: {
        title: lastPageTitle,
        group: sectionTitle,
      },
      preventDefault: true,
      stopPropagation: true,
    },
  ];

  // eslint-disable-next-line
  // @ts-ignore
  const last = firstRow + paginatedLength;

  return (
    // eslint-disable-next-line
    // @ts-ignore
    <KeyActions actions={shortcuts}>
      <Flex alignItems="center" justifyContent="flex-end">
        <aside className="pagination">
          {!hideFirstPageOption && (
            <Button
              disabled={firstRow === 0}
              icon={<MDIIcon icon={PageFirst} />}
              iconOnly={true}
              onClick={handleFirstPage}
              title={firstPageTitle}
              type="clear"
            />
          )}
          <Button
            disabled={firstRow === 0}
            icon={<MDIIcon icon={ChevronLeft} />}
            iconOnly={true}
            onClick={handlePrevPage}
            title={prevPageTitle}
            type="clear"
          />
          <span className="count">
            {
              // eslint-disable-next-line
              // @ts-ignore
              length > 0 ? firstRow + 1 : 0
            }
            &ndash;{last} {ofPageText} {length}
          </span>
          <Button
            disabled={last === length}
            icon={<MDIIcon icon={ChevronRight} />}
            iconOnly={true}
            onClick={handleNextPage}
            title={nextPageTitle}
            type="clear"
          />
          {!hideLastPageOption && (
            <Button
              disabled={last === length}
              icon={<MDIIcon icon={PageLast} />}
              iconOnly={true}
              onClick={handleLastPage}
              title={lastPageTitle}
              type="clear"
            />
          )}
        </aside>
      </Flex>
    </KeyActions>
  );
};

// Display Name
Pagination.displayName = 'Pagination';

export default Pagination;
